import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Badge, Button, Card } from '../components/ui'
import { routeFor, useFunnelConfig } from '../funnel/useFunnel'
import { eventOnce, funnelApi, initialSessionId } from '../lib/api'
import { useFunnelStore } from '../store/funnel'

/**
 * Waitlist confirmation, plus the deck as a secondary offer.
 *
 * The confirmation leads and the deck sits underneath it — the primary message
 * is "you're in", not "buy something". Framing the deck as *while you wait*
 * keeps it from reading as a paywall on an app we just told them is free.
 *
 * The position is a real count from the database. When the server couldn't
 * assign one we say "we've saved your place" rather than invent a number.
 */
export function WaitlistScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const config = useFunnelConfig()
  const placement = useFunnelStore((state) => state.placement)

  const deck = config.deckOffer
  // Formatted per locale: "€19.99" in English, "19,99 €" in Greek.
  const deckPrice =
    deck.amount === null
      ? null
      : new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-IE', {
          style: 'currency',
          currency: deck.currency,
        }).format(deck.amount)

  useEffect(() => {
    void eventOnce(initialSessionId, 'step_viewed', { step: 'confirm', nodeId: 'confirm' }).catch(() => {})
    if (deck.enabled) void eventOnce(initialSessionId, 'deck_offer_viewed').catch(() => {})
  }, [deck.enabled])

  const openDeck = () => {
    if (!deck.url) return
    void funnelApi.event(initialSessionId, 'deck_offer_clicked').catch(() => {})
    window.open(deck.url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="content-column text-center">
      <div className="confirm-mark" aria-hidden>
        ✓
      </div>
      <p className="eyebrow">{t('waitlist.eyebrow')}</p>
      <h1 className="value-title">{t('waitlist.title')}</h1>
      {placement?.position ? (
        <Badge color="var(--sunshine)" rotate={-3}>
          {t('waitlist.position', { position: placement.position })}
        </Badge>
      ) : (
        <p className="body-copy">{t('waitlist.positionUnknown')}</p>
      )}
      <p className="body-copy">{t('waitlist.body')}</p>

      {deck.enabled && deck.url ? (
        <>
          <hr className="soft-rule" />
          <Card accent="var(--cloud)">
            <p className="eyebrow">{t('waitlist.deck.eyebrow')}</p>
            <h2 className="offer-title">{t('waitlist.deck.title')}</h2>
            <p className="deck-name">{t('waitlist.deck.title2')}</p>
            <p className="body-copy">{t('waitlist.deck.body')}</p>
            {deckPrice ? <p className="offer-price">{deckPrice}</p> : null}
            <Button block size="lg" variant="accent" onClick={openDeck}>
              {t('waitlist.deck.cta')}
            </Button>
            <p className="fine-print">{t('waitlist.deck.note')}</p>
          </Card>
        </>
      ) : null}

      <button className="text-button" onClick={() => navigate(routeFor('handoff'), { replace: true })}>
        {t('waitlist.skip')}
      </button>
    </div>
  )
}
